<template>
    <div class="roster">
        <ul v-if="rows.length > 0" class="roster__list">
            <li v-for="entry in rows" :key="entry._id" class="roster__row">
                <RouterLink class="roster__link" :to="linkToOne(entry)">
                    {{ entry.name }}
                </RouterLink>
                <small v-if="entry.slug" class="roster__slug">{{ entry.slug }}</small>
                <Tag
                    v-if="entry.status !== 'active'"
                    :value="entry.status"
                    severity="secondary"
                />
            </li>
        </ul>

        <p v-else-if="!busy && failure === null" class="roster__empty">
            None. Nothing names this Operation.
        </p>

        <ProgressSpinner v-if="busy && rows.length === 0" class="roster__loading" />

        <div v-if="rows.length > 0" class="roster__footer">
            <span class="roster__count">Showing {{ rows.length }} of {{ total }}.</span>
            <Button
                v-if="rows.length < total"
                label="Show more"
                severity="secondary"
                variant="text"
                size="small"
                :loading="busy"
                @click="loadNextPage"
            />
            <RouterLink class="roster__link" :to="linkToAll()">
                See all {{ total }} in {{ row.label.toLowerCase() }}
            </RouterLink>
        </div>

        <Message v-if="failure" severity="error" :closable="false" class="roster__failure">
            {{ failureText(failure) }}
        </Message>
    </div>
</template>

<script setup>
import Button from 'primevue/button'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'
import Tag from 'primevue/tag'
import { ref, watch } from 'vue'
import { RouterLink } from 'vue-router'

import { failureText } from '@/lib/failureText.js'

// What an Operation manages, read from the collection that holds it.
//
// Nothing here writes, and that is the model rather than a shortcut: an
// Operation records nothing about what it manages, so there is no roster to add
// to. A house moves by naming a different Operation on its own screen, which is
// one write on one document — the row an editor here would have to duplicate.
const props = defineProps({
    row: { type: Object, required: true },
    record: { type: Object, required: true },
    store: { type: Object, required: true },
    // Declared and ignored. The panel hands the same flag to every editor, and
    // this one edits nothing — it reads what an Operation manages and links to
    // it, so it is already the view the others become. Taking the prop keeps it
    // off the root element as a stray attribute.
    // eslint-disable-next-line vue/no-unused-properties
    editing: { type: Boolean, default: false }
})

// Enough to fill the rail without asking for a collection. `Show more` reaches
// the rest, and the count says how much rest there is.
const PAGE_SIZE = 25

/**
 * The pages read so far, copied out of the store rather than read through it.
 *
 * The store holds one page and replaces it on every load, so reading it in the
 * template would make `Show more` swap the list instead of growing it — and it
 * would let a load for another Operation land under this heading, the way a
 * held `referencing` list once did. A local copy answers both.
 */
const rows = ref([])
const total = ref(0)
const page = ref(0)

// Its own flag, not the store's. `store.loading` is set by every read the
// screen makes, so binding to it spun this spinner while somebody typed in the
// search bar.
const busy = ref(false)
const failure = ref(null)

async function loadNextPage() {
    const next = page.value + 1
    busy.value = true
    failure.value = null
    const ok = await props.row.load(props.store, props.record._id, {
        page: next,
        pageSize: PAGE_SIZE
    })
    busy.value = false
    if (ok === false) {
        failure.value = props.store.error
        return
    }
    page.value = next
    rows.value = [...rows.value, ...props.row.readPage(props.store)]
    total.value = props.row.readCount(props.store)
}

// Read when the panel opens, and again whenever it is handed a different
// Operation — the instance panel keeps one component across records, so
// without this the second Operation shows the first one's houses.
watch(
    () => props.record._id,
    function () {
        rows.value = []
        total.value = 0
        page.value = 0
        failure.value = null
        loadNextPage()
    },
    { immediate: true }
)

/**
 * One entry, on the screen that holds it.
 *
 * There is no route to a single document — the collection screens hold which
 * record is open in their own state — so the link narrows that screen to this
 * one instead. `_id:<hex>` is FQL the collection's surface accepts, and it
 * converts to an `$oid` match.
 */
function linkToOne(entry) {
    return { name: props.row.routeName, query: { q: `_id:${entry._id}` } }
}

// The same screen, narrowed to everything this Operation manages. Matches the
// index the children carry for this question.
function linkToAll() {
    return {
        name: props.row.routeName,
        query: { q: `operationInfo.operationId:${props.record._id}` }
    }
}
</script>

<style scoped>
.roster__list {
    list-style: none;
    margin: 0 0 8px;
    padding: 0;
}

.roster__row {
    align-items: baseline;
    border-bottom: 1px solid var(--p-content-border-color);
    display: flex;
    gap: 8px;
    padding: 6px 0;
}

.roster__link {
    color: var(--p-primary-color);
    font-size: 13px;
    text-decoration: none;
}

.roster__link:hover {
    text-decoration: underline;
}

.roster__row .roster__link {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.roster__slug {
    color: var(--p-surface-400);
    font-size: 11px;
}

.roster__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 0;
}

.roster__loading {
    height: 32px;
    width: 32px;
}

/* Stacked, like the rows above it. The instance panel is a narrow rail, and a
   count, a button and a link in one line wrap into three ragged ones. */
.roster__footer {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.roster__count {
    color: var(--p-surface-400);
    font-size: 11px;
}

.roster__failure {
    margin: 6px 0 0;
}
</style>
